import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { employeeIncidentBulkApproveService } from '@/api/domains/employeeIncident';
import type { LC_BulkApproveIncidentsOut } from '@/api/domains/employeeIncident/types';
import { useUI } from '@/hooks/ui/useUI';
import type { IncidentBulkApprovalModalFilters } from './useIncidentBulkApprovalModalList';


interface UseIncidentBulkApprovalParams {
  selectedIncidentIds: number[];
  onSuccess: (result: LC_BulkApproveIncidentsOut) => void;
  /**
   * Modo "seleccionar todo" (ASIS-5249): cuando es `true`, se ignora `selectedIncidentIds` y
   * se envían los filtros vigentes + los IDs excluidos manualmente — el backend resuelve el
   * conjunto real de incidencias Pendientes a aprobar al momento de ejecutar.
   */
  isSelectAllMode?: boolean;
  filters?: IncidentBulkApprovalModalFilters;
}

export interface UseIncidentBulkApprovalResult {
  comment: string;
  setComment: (value: string) => void;
  canSubmit: boolean;
  isSubmitting: boolean;
  handleSubmit: () => Promise<void>;
}

/**
 * Lógica para la aprobación masiva de incidentes de marcaje (ASIS-5249).
 * En modo lista explícita, envía los IDs seleccionados como CSV. En modo "seleccionar todo",
 * envía los filtros vigentes (dateFrom/dateTo/errorTypeId/filterEmployeeId/searchText) más los
 * IDs excluidos manualmente, y el backend resuelve el conjunto real al momento de ejecutar.
 * Endpoint: PUT /v1/attendances/incidents/bulk-approval.
 */
export function useIncidentBulkApproval({
  selectedIncidentIds,
  onSuccess,
  isSelectAllMode = false,
  filters,
}: UseIncidentBulkApprovalParams): UseIncidentBulkApprovalResult {
  const { t: translate } = useTranslation();
  const { showApiError, showSuccess, showWarning } = useUI();

  const [comment, setComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submittingRef = useRef(false);

  const canSubmit = comment.trim().length > 0 && !isSubmitting;

  const handleSubmit = useCallback(async () => {
    if (submittingRef.current || comment.trim().length === 0) return;
    submittingRef.current = true;
    setIsSubmitting(true);
    try {
      const response = await employeeIncidentBulkApproveService.bulkApprove(
        isSelectAllMode
          ? {
            approveAll: true,
            approverComment: comment.trim(),
            dateFrom: filters?.dateFrom ?? null,
            dateTo: filters?.dateTo ?? null,
            errorTypeId: filters?.errorTypeId ?? null,
            filterEmployeeId: filters?.filterEmployeeId ?? null,
            searchText: filters?.searchText || null,
            excludeIncidentIds: filters?.excludeIncidentIds?.length
              ? filters.excludeIncidentIds.join(',')
              : undefined,
          }
          : {
            incidentIds: selectedIncidentIds.join(','),
            approverComment: comment.trim(),
          },
      );

      if (!response.success || !response.data) {
        showApiError(response, translate('attendance:incidentManagement.messages.approveError'));
        throw new Error('bulk-approve-incidents-failed');
      }

      const result = response.data;

      // Caso no-op (ASIS-5249, modo "seleccionar todo"): el conjunto resuelto por el backend
      // quedó vacío — ej. otro usuario ya resolvió todo lo pendiente entre la activación de la
      // selección y la confirmación, o se excluyó todo manualmente. totalAlreadyResolved === 0
      // por sí solo no distingue este caso de un éxito real — hay que mirar también totalApproved.
      if (result.totalApproved === 0 && result.totalAlreadyResolved === 0) {
        showWarning(translate('attendance:incidentManagement.bulk.nothingToApprove'));
      } else if (result.totalAlreadyResolved === 0) {
        showSuccess(translate('attendance:incidentManagement.bulk.approveSuccess'));
      } else {
        showWarning(
          translate('attendance:incidentManagement.bulk.partialResult', {
            processed: result.totalApproved,
            skipped: result.totalAlreadyResolved,
          }),
        );
      }

      onSuccess(result);
      setComment('');
    } finally {
      submittingRef.current = false;
      setIsSubmitting(false);
    }
  }, [
    comment,
    selectedIncidentIds,
    isSelectAllMode,
    filters,
    showApiError,
    showSuccess,
    showWarning,
    translate,
    onSuccess,
  ]);

  return {
    comment,
    setComment,
    canSubmit,
    isSubmitting,
    handleSubmit,
  };
}

export default useIncidentBulkApproval;
